import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden">
      <button
        onClick={() => navigate('/login')}
        className="absolute top-6 left-6 z-20 flex items-center space-x-2 text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm"
        data-testid="back-to-login-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Login</span>
      </button>

      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&q=80"
          alt="Books background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-900/30" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-black/80 backdrop-blur-lg p-8 rounded-lg shadow-2xl border border-white/10">
          <div className="flex items-center justify-center mb-8">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
              alt="Seven Chapter Books"
              className="h-28 sm:h-32 w-auto cursor-pointer"
              style={{ filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.85))', imageRendering: 'auto' }}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onClick={() => navigate('/')}
            />
          </div>

          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Check Your Email</h2>
              <p className="text-gray-400 mb-6">
                If an account with that email exists, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6"
                data-testid="back-to-login-after-reset"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white text-center mb-2">Forgot Password?</h2>
              <p className="text-gray-400 text-center text-sm mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 focus:ring-red-600"
                      required
                      data-testid="forgot-password-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 transition-all"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
